/**
 * PROMISE MODULE SERVICE
 * 
 * Manages user's selected promise (7/14/30 days)
 * Tracks promise progress and countdown
 * Links to streak logic
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';
import { getPromiseText } from '../constants/coreMessaging';
import i18n from '../i18n/index';

export type PromiseDays = 7 | 14 | 30;

export interface PromiseProgress {
  daysSelected: PromiseDays;
  startDate: string; // ISO date string
  daysRemaining: number;
  daysCompleted: number;
  progressPercent: number;
  promiseText: string;
}

/**
 * Set user's promise
 * Called during onboarding
 */
export const setUserPromise = async (
  userId: string,
  days: PromiseDays
): Promise<void> => {
  const userRef = doc(db, "users", userId);
  const today = new Date().toISOString().split('T')[0];
  
  await updateDoc(userRef, {
    promiseDays: days,
    promiseStartDate: today
  });
  
  console.log(`[PROMISE] Set promise for user ${userId}: ${days} days`);
};

/**
 * Get promise progress for user
 */
export const getPromiseProgress = async (
  user: User
): Promise<PromiseProgress | null> => {
  if (!user.promiseDays || !user.promiseStartDate) {
    return null;
  }

  const startDate = new Date(user.promiseStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  const daysSelected = user.promiseDays;
  const daysCompleted = Math.max(0, Math.min(diffDays, daysSelected));
  const daysRemaining = Math.max(0, daysSelected - daysCompleted);
  const progressPercent = (daysCompleted / daysSelected) * 100;
  
  const currentLang = (i18n.language || 'en') as any;
  const promiseText = getPromiseText(currentLang, daysSelected);

  return {
    daysSelected,
    startDate: user.promiseStartDate,
    daysRemaining,
    daysCompleted,
    progressPercent,
    promiseText
  };
};

/**
 * Check if promise is completed
 */
export const isPromiseCompleted = (user: User): boolean => {
  if (!user.promiseDays || !user.promiseStartDate) return false;
  
  const startDate = new Date(user.promiseStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays >= user.promiseDays;
};

